/**
 * 개발 도구 — 연동 점검 (/adm/dev/health) — 외부 연동(문자·PG·OCR …) 상태와 연동 정보 메모
 * ($j.Pages.Admin.DevHealth / body data-page-name="DevHealth")
 *
 * 점검 대상·OCR 종류·연동 메모는 컨트롤러가 #page-data 로 넘긴다.
 * 보기가 둘이다 — **카드**는 연동 하나의 상태·연동 정보(메모), **목록**은 요금(충전식 잔고·사용액)을 한 표로.
 * 잔고 추정은 서버(VendorWalletService)가 하고 여기는 그리기·입력(모달)만 한다.
 */
export default class DevHealth {
    initialize() { initialize(); }
}

function initialize() {
    const PAGE      = JSON.parse(document.getElementById('page-data').textContent);
    const TARGETS   = PAGE.targets;
    const OCR_TYPES = PAGE.ocrTypes;
    const DOCS      = PAGE.docs;   // 연동키 → {memo, creds, updated_at, updated_admin_num}
    const STATE_LABEL = { ok: '정상', warn: '주의', bad: '장애', none: '기록없음', off: '미설정', wait: '확인중', info: '정보', plan: '연동 예정' };

    const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    const won = (n) => Number(n || 0).toLocaleString() + '원';
    /** 잔고 값 하나 — 잔량 단위(meter)가 개수면 '개', 아니면 '원' */
    const amt = (meter, n) => (meter === 'count' ? Number(n || 0).toLocaleString() + '개' : won(n));

    const ago = (min) => {
        if (min == null) return '-';
        if (min < 1) return '방금';
        if (min < 60) return min + '분 전';
        if (min < 60 * 48) return Math.floor(min / 60) + '시간 전';
        return Math.floor(min / 1440) + '일 전';
    };

    /* ── 골격 ─────────────────────────────────────── */

    function probeHtml(t) {
        if (t.probe === 'upload') {
            const opts = Object.keys(OCR_TYPES).map((k) => `<option value="${k}">${esc(OCR_TYPES[k])}</option>`).join('');
            return `<div class="hc-probe">
                        <select class="form-control form-control-sm" data-ocr-type>${opts}</select>
                        <input type="file" data-ocr-file accept=".jpg,.jpeg,.png,.pdf,.tif,.tiff">
                        <button type="button" class="btn btn-sm btn-default" data-ocr-run>서류 읽기</button>
                        <span class="hc-cost">${esc(t.cost)} · 사진은 저장하지 않는다</span>
                    </div>
                    <div class="hc-result" data-result></div>`;
        }
        if (t.probe === 'auto' || t.probe === 'button') {
            return `<div class="hc-probe">
                        <button type="button" class="btn btn-sm btn-default" data-probe>점검 호출</button>
                        <span class="hc-cost">${esc(t.cost)}</span>
                    </div>
                    <div class="hc-result" data-result></div>`;
        }
        return '';
    }

    /* ── 목록 보기 — 요금 ─────────────────────────── */

    const W_STATE = { ok: '충분', warn: '주의', bad: '부족', paid: '유료 전환', none: '미입력', wait: '확인중' };
    const W_KIND  = { set: '실잔고 확인', charge: '충전' };
    const setLabel = (meter) => (meter === 'count' ? '남은 개수 확인' : W_KIND.set);
    let ITEMS = {};   // 마지막으로 받은 판정 — 목록·잔고 모달이 읽는다

    const BILL_MODES = PAGE.billModes || {};

    /**
     * 정렬 — 머리글을 누르면 그 칸으로, 다시 누르면 뒤집는다. 처음은 충전식이 위(이 보기의 주인공이 요금이다).
     * 수·심각도 칸은 큰 것(나쁜 것)부터, 글 칸은 가나다순부터 시작한다. 값이 없는 줄은 방향과 무관하게 맨 아래
     */
    const SEVERITY = { bad: 4, warn: 3, none: 2, paid: 1, ok: 1 };
    const COLS = [
        { key: 'name',    title: '연동',         text: true, val: (t) => t.name },
        { key: 'call',    title: '호출',         cls: 'text-center', val: (t, st) => SEVERITY[st.state] || 0 },
        { key: 'bill',    title: '과금',         text: true, val: (t, st) => BILL_MODES[(st.bill || {}).mode] || '' },
        // 잔고(원·개)와 잔여(개)가 같은 칸을 쓴다 — 한 연동은 둘 중 하나다(가상계좌만 잔여)
        { key: 'wallet',  title: '잔고·잔여',    cls: 'text-center', val: (t, st) => { const g = gauge(st); return g ? SEVERITY[g.state] || 0 : null; } },
        { key: 'balance', title: '추정 잔고·잔여', cls: 'hc-num', val: (t, st) => (st.stock ? st.stock.remaining : (st.wallet || {}).balance ?? null) },
        { key: 'daily',   title: '하루 사용',    cls: 'hc-num', val: (t, st) => (st.stock ? st.stock.daily : ((st.wallet || {}).balance !== undefined ? st.wallet.daily : null)) },
        { key: 'left',    title: '남은 기간',    cls: 'hc-num', val: (t, st) => (gauge(st) || {}).days_left ?? null },
        { key: 'month',   title: '이번 달 사용', cls: 'hc-num', val: (t, st) => (gauge(st) || {}).month ?? null },
    ];
    const SORT = { key: '', dir: 1 };
    /** 그 연동이 바닥나는 것 — 잔여(개) 또는 잔고(원). 둘 다 없으면 null */
    const gauge = (st) => {
        const w = st.wallet;
        // 안심번호처럼 둘 다 있으면 잔고가 문제일 때 잔고를 먼저 말한다
        if (st.stock && w && !w.unsupported && (SEVERITY[w.state] > SEVERITY[st.stock.state]) && w.state !== 'none') return w;
        return st.stock || w || null;
    };

    function sortTargets(list) {
        const col = COLS.find((c) => c.key === SORT.key);
        const idx = (t) => TARGETS.indexOf(t);
        if (!col) {
            const pre = (t) => (((ITEMS[t.key] || {}).bill || {}).mode === 'prepaid' ? 0 : 1);
            return list.slice().sort((a, b) => pre(a) - pre(b) || idx(a) - idx(b));
        }
        return list.slice().sort((a, b) => {
            const va = col.val(a, ITEMS[a.key] || {}), vb = col.val(b, ITEMS[b.key] || {});
            const ea = va === null || va === '', eb = vb === null || vb === '';
            if (ea || eb) return (ea - eb) || idx(a) - idx(b);
            const c = col.text ? String(va).localeCompare(String(vb), 'ko') : va - vb;
            return c * SORT.dir || idx(a) - idx(b);
        });
    }

    $('#hcList').on('click', 'th[data-sort]', function () {
        const k = this.dataset.sort;
        const col = COLS.find((c) => c.key === k);
        if (SORT.key === k) SORT.dir = -SORT.dir;
        else { SORT.key = k; SORT.dir = col.text ? 1 : -1; }
        renderList();
    });

    /**
     * 한 줄 = 연동 하나. 충전식이든 아니든 같은 표·같은 줄 모양이다 — 과금 방식은 「과금」 칸이 말한다.
     * **줄 어디를 눌러도 그 연동의 설정 모달이 뜬다**(과금 방식·단가·잔고·연동 정보). 충전식으로 둔 연동만 잔고 칸이 찬다
     */
    function renderList() {
        const dash = '<span class="hc-cost">-</span>';

        const billCell = (t) => {
            const b = (ITEMS[t.key] || {}).bill || {};
            const label = BILL_MODES[b.mode] ? esc(BILL_MODES[b.mode]) : '<span class="hc-cost">미정</span>';
            return `${label}${b.note ? `<div class="hc-cost">${esc(b.note)}</div>` : ''}`;
        };

        /** 비즈고 — 월·일 전 채널 합계 / 한도. 단위별 건수는 그 아래 한 줄 */
        const totalLine = (w) => {
            if (!w.total) return '';
            const n = (v) => Number(v).toLocaleString();
            const units = (w.units || []).map((u) => `${esc(u.name)} ${n(u.used)}`).join(' / ');
            return `<div><b>월 ${n(w.total.used)} / ${n(w.total.limit)}</b></div>
                    <div><b>일 ${n(w.total.today)} / ${n(w.total.day_limit)}</b></div>
                    ${units ? `<div class="hc-cost">${units}</div>` : ''}`;
        };

        /**
         * 월 요금제의 단위별 사용/제공 + 초기화일 — planRow 와 050 줄이 같이 쓴다.
         * @param solo 단위가 하나인 줄 — 이름은 줄 머리의 연동 이름으로 충분하다. '24 / 100건' 만 두면 24개 남은 것으로 읽히므로 남은 개수를 앞세운다
         */
        const planLines = (w, solo = false) => {
            if (w.total) return totalLine(w);
            const lines = (w.units || []).filter((u) => u.quota > 0 || u.used > 0).map((u) => {
                const n = Number(u.used).toLocaleString();
                const name = solo ? '' : `${esc(u.name)} `;
                if (solo && u.quota > 0) {
                    const rest = w.balance !== undefined ? w.balance : Math.max(u.quota - u.used, 0);
                    return `<div><b>남은 ${Number(rest).toLocaleString()}개</b></div>
                            <div class="hc-cost">사용 ${n} / ${Number(u.quota).toLocaleString()}건${u.over > 0 ? ` · 초과 ${Number(u.over).toLocaleString()}` : ''}</div>`;
                }
                return u.quota > 0
                    ? `<div>${name}${n} / ${Number(u.quota).toLocaleString()}건${u.over > 0 ? ` <span class="hc-cost">초과 ${Number(u.over).toLocaleString()}</span>` : ''}</div>`
                    : `<div class="hc-cost">${name}${n}건</div>`;
            }).join('');
            return lines || dash;
        };

        const row = (t) => {
            const st = ITEMS[t.key] || { state: 'wait' };
            const w  = st.wallet;
            const stateBadge = `<span class="hc-state is-${st.state}">${STATE_LABEL[st.state] || st.state}</span>`;
            const head = `<td><div class="hc-name">${esc(t.name)}</div><div class="hc-vendor">${esc(t.vendor)}</div></td>
                          <td class="text-center">${stateBadge}</td><td>${billCell(t)}</td>`;

            const k = st.stock;
            if (k) {
                const u = esc(k.unit);
                if (k.pool) {
                    // 충전식·월 요금제면 잔고도 같은 줄에 — 번호 사용량과 따로 바닥난다. 단위는 저장된 잔량 단위를 따른다
                    const wl = st.wallet && !st.wallet.unsupported ? st.wallet : null;
                    const plan = !!(wl && wl.plan);
                    const isCount = !!(wl && wl.meter === 'count');
                    const hasBal = wl && !plan && wl.balance !== undefined;   // 월 요금제에는 기준점(base)이 없다
                    const wName = plan ? '제공' : (isCount ? '개수' : '잔고');
                    // 개수면 이번 달 사용이 '건 연결' 과 같은 수다 — 돈일 때만 덧붙인다
                    const mw = wl && wl.month !== null && wl.month !== undefined && (plan || !isCount) ? `<div class="hc-cost">약 ${won(wl.month)}</div>` : '';
                    const wBadge = wl ? `<div><span class="hc-state is-${wl.state}" title="${esc(wl.message || '')}">${wName} ${W_STATE[wl.state] || wl.state}</span></div>` : '';
                    let wLine = '';
                    if (hasBal) {
                        wLine = `<div>${wName} ${amt(wl.meter, wl.balance)}</div>`;
                    } else if (plan) {
                        wLine = planLines(wl);
                    }
                    const dailyCell = hasBal ? '약 ' + amt(wl.meter, wl.daily) : (plan && wl.units && wl.resets_at ? `약 ${wl.daily}건` : dash);
                    const hasLeft = (hasBal || plan) && wl.days_left !== null && wl.days_left !== undefined;
                    return `<tr data-key="${esc(t.key)}" class="is-${(gauge(st) || k).state}">${head}
                        <td class="text-center"><span class="hc-state is-${k.state}" title="${esc(k.message)}">사용 ${W_STATE[k.state] || k.state}</span>${wBadge}</td>
                        <td class="hc-num"><b>사용 ${Number(k.in_use).toLocaleString()} / ${Number(k.total).toLocaleString()}${u}</b>${wLine}</td>
                        <td class="hc-num">${dailyCell}</td><td class="hc-num">${hasLeft ? `약 ${wl.days_left}일` : dash}</td>
                        <td class="hc-num">${Number(k.month).toLocaleString()}건 연결${mw}</td></tr>`;
                }
                return `<tr data-key="${esc(t.key)}" class="is-${k.state}">${head}
                    <td class="text-center"><span class="hc-state is-${k.state}" title="${esc(k.message)}">잔여 ${W_STATE[k.state] || k.state}</span></td>
                    <td class="hc-num"><b>${Number(k.remaining).toLocaleString()}${u}</b></td>
                    <td class="hc-num">약 ${k.daily}${u}</td>
                    <td class="hc-num">${k.days_left !== null ? `약 ${Number(k.days_left).toLocaleString()}일` : dash}</td>
                    <td class="hc-num">${Number(k.month).toLocaleString()}${u} 발급</td></tr>`;
            }
            if (!w) {
                return `<tr data-key="${esc(t.key)}">${head}
                    <td class="text-center">${dash}</td><td colspan="4"></td></tr>`;
            }
            if (w.unsupported) {
                return `<tr data-key="${esc(t.key)}">${head}
                    <td class="text-center"><span class="hc-state is-none" title="${esc(w.message)}">추정 불가</span></td>
                    <td colspan="4"></td></tr>`;
            }
            if (w.plan) return planRow(t, w, head);
            const has = w.balance !== undefined;
            const overCost = w.over_cost === null ? '단가 없음' : '약 ' + won(w.over_cost);
            return `<tr data-key="${esc(t.key)}" class="is-${w.state}">${head}
                <td class="text-center"><span class="hc-state is-${w.state}" title="${esc(w.message || '')}">${W_STATE[w.state] || w.state}</span></td>
                <td class="hc-num">${totalLine(w)}<b>${has ? amt(w.meter, w.balance) : dash}</b>
                    ${w.over > 0 ? `<div class="hc-cost">유료 ${amt('count', w.over)} · ${overCost}</div>` : ''}</td>
                <td class="hc-num">${has ? '약 ' + amt(w.meter, w.daily) : dash}</td>
                <td class="hc-num">${has && w.days_left !== null && w.state !== 'paid' ? `약 ${w.days_left}일` : dash}</td>
                <td class="hc-num">${w.month === null ? '<span class="hc-cost">단가 없음</span>' : amt(w.meter, w.month)}</td></tr>`;
        };

        /** 월 요금제 — 단위별 사용/제공을 한 줄씩, 요금 칸은 이번 달 예상 */
        const planRow = (t, w, head) => {
            const has = w.units && w.resets_at;
            return `<tr data-key="${esc(t.key)}" class="is-${w.state}">${head}
                <td class="text-center"><span class="hc-state is-${w.state}" title="${esc(w.message || '')}">${W_STATE[w.state] || w.state}</span></td>
                <td class="hc-num">${planLines(w, (w.units || []).length === 1)}</td>
                <td class="hc-num">${has ? `약 ${w.daily}건` : dash}</td>
                <td class="hc-num">${has && w.days_left !== null && w.days_left !== undefined ? `약 ${w.days_left}일` : dash}</td>
                <td class="hc-num">${w.month === null || w.month === undefined ? '<span class="hc-cost">단가 없음</span>' : `약 ${won(w.month)}`}</td></tr>`;
        };

        const th = COLS.map((c) => {
            const on = SORT.key === c.key;
            const ic = on ? (SORT.dir > 0 ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort';
            return `<th class="${c.cls || ''}${on ? ' is-sorted' : ''}" data-sort="${c.key}">${c.title} <i class="fas ${ic}"></i></th>`;
        }).join('');

        $('#hcList').html(`
            <table class="hc-table">
                <thead><tr>${th}</tr></thead>
                <tbody>${sortTargets(TARGETS.filter((t) => !t.info)).map(row).join('')}</tbody>
            </table>
            <p class="hc-list__note">추정 잔고 = 마지막 실잔고 + 그 뒤 충전 − 그 뒤 사용량(발송·호출 기록 × 단가).
                업체 화면에서 실잔고를 볼 때마다 줄을 눌러 [잔고 입력 → 실잔고 확인] 으로 맞추면 어긋남이 0 이 된다.
                하루 사용은 최근 7일 평균. 개수로 남는 충전식은 같은 식을 개수로 센다(사용 1건 = 1개) — 다 쓰면 유료로 넘어가는 연동은 초과 개수와 금액을 함께 보여 준다.
                월 요금제는 이번 달 기록으로 센다 — 예상 요금 = 월 기본요금 + 단위별 (사용 − 제공) × 단가.
                가상계좌는 돈이 아니라 <b>남은 계좌 수</b>(잔여)이고 DB 에서 바로 센다 — 하루 사용은 최근 90일 평균 발급 수.</p>`);
    }

    /**
     * 연동 설정 — 모달 하나에 전부. 과금 방식 · 단가 · 잔고 입력 · 연동 정보(메모).
     *
     * 단가·잔고 칸은 **방식이 충전식이고 사용량을 셀 수 있는 연동**(t.wallet)일 때만 보인다 —
     * 방식을 고르는 즉시 열리고 닫힌다. [저장] 은 설정(방식·단가·메모)을 한 번에 쓰고,
     * 잔고칸에 값이 있으면 이어서 원장에도 한 줄 쌓는다. 저장 버튼은 이 하나뿐이다.
     *
     * @param focus 'doc' 이면 연동 정보 칸으로 바로 간다(카드의 [편집])
     */
    function openSettings(key, focus = '') {
        const t = TARGETS.find((x) => x.key === key);
        const st = ITEMS[key] || {};
        const b = st.bill || {};
        // 판정이 없는 방식(충전식 아님)이어도 저장된 설정으로 칸을 채운다 — 비워 두면 [저장] 이 0 으로 덮어쓴다
        const w = st.wallet || st.wallet_cfg || {};
        const d = DOCS[key] || {};
        const k = st.stock;
        const savedMeter = w.meter || 'won';

        const modes = `<option value="">미정</option>` + Object.keys(BILL_MODES).map((k) =>
            `<option value="${esc(k)}"${k === b.mode ? ' selected' : ''}>${esc(BILL_MODES[k])}</option>`).join('');
        const prices = t.wallet ? Object.keys(t.wallet).map((u) => `
            <label class="hc-wallet__price" data-w-price-row><span>${esc(t.wallet[u])}</span>
                <input type="number" min="0" step="any" class="form-control form-control-sm" data-w-price="${esc(u)}"
                       value="${esc((w.prices || {})[u] || '')}"> 원</label>`).join('') : '';
        const quotaRows = t.wallet_countable ? Object.keys(t.wallet).map((u) => `
            <label class="hc-wallet__price" data-w-quota-row><span>${esc(String(t.wallet[u]).replace(' 1건', ''))} 월 제공</span>
                <input type="number" min="0" class="form-control form-control-sm" data-w-quota="${esc(u)}"
                       value="${esc((w.quotas || {})[u] || '')}"> 건</label>`).join('') : '';
        const logs = (w.logs || []).map((l) => `
            <tr><td>${esc(String(l.created_at).slice(0, 16))}</td><td>${esc(l.kind === 'set' ? setLabel(l.meter) : (W_KIND[l.kind] || l.kind))}</td>
                <td class="hc-wallet__amt">${amt(l.meter, l.amount)}</td><td>${esc(l.memo)}</td></tr>`).join('');

        const m = $j.Ui.Modal.open({
            title: `${esc(t.name)} <small class="hc-set__vendor">${esc(t.vendor)}</small>`,
            html: `<div class="hc-wmodal">
                ${t.manual ? `
                <section class="hc-set">
                    <h4 class="hc-set__h">업체</h4>
                    ${customFields(t)}
                </section>` : ''}

                <section class="hc-set">
                    <h4 class="hc-set__h">과금</h4>
                    <label class="hc-wallet__price"><span>방식</span>
                        <select class="form-control form-control-sm" data-b-mode>${modes}</select></label>
                    <label class="hc-wallet__price"><span>메모</span>
                        <input type="text" class="form-control form-control-sm hc-wallet__note" data-b-note maxlength="100"
                               value="${esc(b.note || '')}" placeholder="예: 인증 건당 · 매월 10일 청구"></label>
                    ${t.wallet ? '' : '<div class="hc-wallet__hint">이 연동은 사용량을 셀 기록이 없어 충전식으로 두어도 잔고를 추정하지 않는다.</div>'}
                </section>

                ${t.wallet ? `
                <section class="hc-set" data-w-plan-sec>
                    <h4 class="hc-set__h">${t.wallet_countable ? '잔량·단가' : '단가'}</h4>
                    ${t.wallet_countable ? `
                    <label class="hc-wallet__price" data-w-meter-row><span>잔량 단위</span>
                        <select class="form-control form-control-sm" data-w-meter>
                            <option value="won"${savedMeter === 'won' ? ' selected' : ''}>금액 (원)</option>
                            <option value="count"${savedMeter === 'count' ? ' selected' : ''}>개수</option>
                        </select></label>
                    <label class="hc-wallet__price" data-w-count-row><span>매월 초기화</span>
                        <input type="checkbox" data-w-reset${w.reset === 'month' ? ' checked' : ''}> 매월 1일에 다시 채운다</label>
                    <label class="hc-wallet__price" data-w-base-row><span>월 기본요금</span>
                        <input type="number" min="0" class="form-control form-control-sm" data-w-base value="${esc(w.base_fee || '')}"> 원</label>
                    <label class="hc-wallet__price" data-w-count-row><span>다 쓰면</span>
                        <select class="form-control form-control-sm" data-w-after>
                            <option value="stop"${w.after !== 'paid' ? ' selected' : ''}>멈춤</option>
                            <option value="paid"${w.after === 'paid' ? ' selected' : ''}>유료 전환</option>
                        </select></label>
                    <div class="hc-wallet__hint" data-w-switch-hint hidden>단위를 바꾸면 [저장] 뒤에 그 단위로 새로 입력한다. 이전 단위의 기록은 계산에 쓰지 않는다.</div>` : ''}
                    ${prices}
                    ${quotaRows}
                    <label class="hc-wallet__price"><span>경고</span>
                        <input type="number" min="1" class="form-control form-control-sm" data-w-warn value="${esc(w.warn_days || 7)}"> 일 남으면 주의</label>
                    <div class="hc-wallet__hint" data-w-price-hint></div>
                </section>

                <section class="hc-set" data-prepaid data-w-entry>
                    <h4 class="hc-set__h">${savedMeter === 'count' ? '남은 개수 입력' : '잔고 입력'} ${w.balance !== undefined ? `<small>추정 ${amt(savedMeter, w.balance)}</small>` : ''}</h4>
                    <div class="hc-wallet__row">
                        <select class="form-control form-control-sm" data-w-kind>
                            <option value="set">${setLabel(savedMeter)}</option><option value="charge">${W_KIND.charge}</option>
                        </select>
                        <input type="text" inputmode="numeric" class="form-control form-control-sm" data-w-amount data-amount-kor="${savedMeter === 'count' ? '개' : ''}" placeholder="${savedMeter === 'count' ? '개수' : '금액'}">
                        <input type="text" class="form-control form-control-sm" data-w-memo maxlength="200" placeholder="메모 (선택)">
                    </div>
                    <div class="hc-wallet__hint"><b>${setLabel(savedMeter)}</b> — 업체 화면에 보이는 지금 ${savedMeter === 'count' ? '남은 개수' : '잔고'}. 이 시각부터 사용량을 새로 센다.
                        <span data-w-plan-hint>이번 달에만 쓰이고, 다음 달 1일에 월 제공으로 돌아간다.</span>
                        <b>충전</b> — 넣은 ${savedMeter === 'count' ? '개수' : '금액'}. 마지막 확인 값에 더한다. 맨 아래 [저장] 을 누르면 설정과 함께 쌓이고, 비워 두면 쌓지 않는다.</div>
                    ${logs ? `<details><summary class="hc-sum-plain">입력 기록 (최근 10건)</summary><table class="hc-wallet__logs">${logs}</table></details>` : ''}
                </section>` : ''}

                ${k && k.pool ? `
                <section class="hc-set">
                    <h4 class="hc-set__h">번호 풀 <small>${Number(k.total).toLocaleString()}개 중 ${Number(k.in_use).toLocaleString()}개 사용 중</small></h4>
                    <label class="hc-wallet__price"><span>주의</span>
                        <input type="number" min="1" class="form-control form-control-sm" data-k-warn value="${esc(k.warn_count)}"> 개 사용 중이면</label>
                    <div class="hc-wallet__hint">총량·사용 중은 DB 에서 바로 센다. 연결은 4시간 뒤 풀리므로 지금 값이다.</div>
                </section>` : ''}

                ${k && !k.pool ? `
                <section class="hc-set">
                    <h4 class="hc-set__h">잔여 <small>${esc(k.what)} ${Number(k.remaining).toLocaleString()}${esc(k.unit)} · 하루 약 ${k.daily}${esc(k.unit)}</small></h4>
                    <label class="hc-wallet__price"><span>주의</span>
                        <input type="number" min="1" class="form-control form-control-sm" data-k-warn value="${esc(k.warn_days)}"> 일 남으면</label>
                    <label class="hc-wallet__price"><span>부족</span>
                        <input type="number" min="1" class="form-control form-control-sm" data-k-bad value="${esc(k.bad_days)}"> 일 남으면</label>
                    <div class="hc-wallet__hint">남은 개수와 소진 속도(최근 90일)는 DB 에서 바로 센다 — 입력할 것이 없다.
                        추가 발급을 받아 넣기까지 걸리는 시간을 생각해 기준을 잡는다.</div>
                </section>` : ''}

                <section class="hc-set">
                    <h4 class="hc-set__h">연동 정보 ${d.updated_at ? `<small>수정 ${esc(d.updated_at)}</small>` : ''}</h4>
                    <textarea class="hc-set__memo" rows="9" data-doc-memo-in placeholder="관리자 사이트 주소 · 계정 · 주의사항">${esc(d.memo || '')}</textarea>
                </section>

                <div class="hc-doc__bar">
                    <button type="button" class="btn btn-sm btn-primary" data-set-save>저장</button>
                    <button type="button" class="btn btn-sm btn-default" data-set-cancel>닫기</button>
                    ${t.manual ? '<button type="button" class="btn btn-sm btn-outline-danger hc-set__del" data-custom-del>삭제</button>' : ''}
                </div>
            </div>`,
            width: '560px',
            maxWidth: 640,
        });
        const $b = m.el;
        $b.find('.hc-wmodal').data('custom-key', key);

        // 고른 잔량 단위 — 단위를 못 고르는 연동(토큰 과금)은 칸이 없다
        const pickedMeter = () => $b.find('[data-w-meter]').val() || 'won';
        const syncPrepaid = () => {
            const mode = $b.find('[data-b-mode]').val();
            const prepaid = mode === 'prepaid';
            const monthly = mode === 'monthly' && !!t.wallet_countable;
            const isCount = prepaid && pickedMeter() === 'count';
            const switched = prepaid && pickedMeter() !== savedMeter;
            const planOn = monthly || (isCount && $b.find('[data-w-reset]').prop('checked'));
            // 월 요금제에서 남은 개수를 적어 맞추는 것은 단위가 하나일 때만 — 어느 단위의 값인지 정해져야 한다
            const planEntry = planOn && !!t.wallet && Object.keys(t.wallet).length === 1 && (prepaid || savedMeter === 'count');
            $b.find('[data-prepaid]').prop('hidden', !prepaid);
            $b.find('[data-w-plan-sec]').prop('hidden', !prepaid && !monthly);
            $b.find('[data-w-meter-row]').prop('hidden', !prepaid);
            $b.find('[data-w-count-row]').prop('hidden', !isCount);
            $b.find('[data-w-quota-row]').prop('hidden', !planOn);
            $b.find('[data-w-base-row]').prop('hidden', !monthly);
            // 다 쓰면 멈추는 개수형은 돈을 셀 일이 없다 — 단가 칸을 접는다(값은 그대로 저장된다)
            const noPrice = isCount && $b.find('[data-w-after]').val() !== 'paid';
            $b.find('[data-w-price-row], [data-w-price-hint]').prop('hidden', noPrice);
            $b.find('[data-w-price-hint]').text(planOn
                ? '단가·기본요금은 부가세 포함. 월 제공을 넘긴 건과 제공이 0 인 단위의 건에 단가가 붙는다. 이번 달 기록으로 센다.'
                : (isCount ? '다 쓴 뒤 건당 내는 금액(부가세 포함). 비워 두면 초과 개수만 보여 준다.'
                           : '업체 요금표의 단가(부가세 포함). 모르는 단위는 0 — 사용량에 들어가지 않는다.'));
            // 잔고는 저장된 단위로 쌓이므로, 단위를 고쳐 놓은 동안에는 입력을 막는다
            $b.find('[data-w-entry]').prop('hidden', planOn ? (!planEntry || switched) : (!prepaid || switched));
            $b.find('[data-w-plan-hint]').prop('hidden', !planOn);
            // 월 요금제의 충전은 계산에 쓰지 않으므로 고르지 못하게 한다
            const $kind = $b.find('[data-w-kind]');
            $kind.find('option[value="charge"]').prop('disabled', planOn);
            if (planOn) $kind.val('set');
            $b.find('[data-w-switch-hint]').prop('hidden', !switched);
        };
        syncPrepaid();
        $b.on('change', '[data-b-mode], [data-w-meter], [data-w-after], [data-w-reset]', syncPrepaid);
        if (focus === 'doc') $b.find('[data-doc-memo-in]').trigger('focus');

        $b.on('click', '[data-set-cancel]', () => m.close());

        // 입력칸이 숨어 있으면(단위 변경 중·월 요금제·충전식 아님) 남은 값이 있어도 쌓지 않는다
        const hasEntry = () => {
            const $entry = $b.find('[data-w-entry]');
            return $entry.length > 0 && !$entry.prop('hidden') && String($b.find('[data-w-amount]').val() || '').trim() !== '';
        };

        $b.on('click', '[data-set-save]', function () {
            const quotas = {};
            $b.find('[data-w-quota]').each((i, el) => { quotas[$(el).data('w-quota')] = $(el).val() || 0; });
            const reset = $b.find('[data-b-mode]').val() === 'prepaid' && pickedMeter() === 'count' && $b.find('[data-w-reset]').prop('checked');
            if (reset && !Object.values(quotas).some((v) => Number(v) > 0)) return $j.Ui.Dialog.alert('월 제공 건수를 넣어주세요');
            // 잘못된 값이면 설정도 쓰기 전에 돌려보낸다 (서버도 같은 판정을 한다)
            if (hasEntry()) {
                const n = String($b.find('[data-w-amount]').val()).trim().replace(/,/g, '');
                if (!/^\d+$/.test(n) || ($b.find('[data-w-kind]').val() === 'charge' && Number(n) === 0)) {
                    return $j.Ui.Dialog.alert('숫자로 넣어주세요 (충전은 1 이상)');
                }
                const planOn = $b.find('[data-b-mode]').val() === 'monthly' || reset;
                if (planOn && !Object.values(quotas).some((v) => Number(v) > 0)) return $j.Ui.Dialog.alert('남은 개수를 맞추려면 월 제공 건수를 넣어주세요');
            }
            const prices = {};
            $b.find('[data-w-price]').each((i, el) => { prices[$(el).data('w-price')] = $(el).val() || 0; });
            const $btn = $(this).prop('disabled', true);
            const unlock = () => $btn.prop('disabled', false);
            $.post('/adm/ajax/dev/health/settings/save', {
                key, mode: $b.find('[data-b-mode]').val(), note: $b.find('[data-b-note]').val(),
                prices, warn_days: $b.find('[data-w-warn]').val() || 0,
                w_meter: pickedMeter(), w_after: $b.find('[data-w-after]').val() || 'stop',
                w_reset: $b.find('[data-w-reset]').prop('checked') ? 'month' : 'none', w_quotas: quotas,
                w_base_fee: $b.find('[data-w-base]').val() || 0,
                stock_warn: $b.find('[data-k-warn]').val() || 0, stock_bad: $b.find('[data-k-bad]').val() || 0,
                memo: $b.find('[data-doc-memo-in]').val(),
                ...(t.manual ? customValues($b) : {}),
            }).done((res) => {
                if (!res.result) { unlock(); return $j.Ui.Dialog.alert(res.message || '저장하지 못했습니다'); }
                DOCS[key] = res.doc;
                if (t.manual) {
                    const v = customValues($b);
                    t.name = v.custom_name; t.vendor = v.custom_vendor; t.use = v.custom_use;
                    renderSkeleton();
                }
                fillDoc(key);
                // 단위를 바꿨으면 그 단위로 새로 입력해야 하니 창을 다시 연다
                const reopen = $b.find('[data-b-mode]').val() === 'prepaid' && pickedMeter() !== savedMeter;
                const finish = (msg) => {
                    m.close();
                    loadStatus().then(() => { if (reopen) openSettings(key); });
                    $j.F.Admin.Util.adminToast('success', msg);
                };
                if (!hasEntry()) { unlock(); return finish('저장했습니다'); }

                // 단위가 저장된 뒤에 쌓아야 하므로 설정 저장 다음에 보낸다. 실패하면 입력값이 남도록 창을 닫지 않는다
                const failEntry = (why) => {
                    loadStatus();
                    $j.Ui.Dialog.alert(`설정은 저장했지만 잔고·개수는 기록하지 못했습니다 — ${why}`);
                };
                $.post('/adm/ajax/dev/health/wallet/add', {
                    key, kind: $b.find('[data-w-kind]').val(), amount: $b.find('[data-w-amount]').val(),
                    memo: $b.find('[data-w-memo]').val(),
                }).done((r) => {
                    if (!r.result) return failEntry(r.message || '다시 시도해 주세요');
                    finish('저장하고 기록했습니다');
                }).fail(() => failEntry('요청 실패'))
                  .always(unlock);
            }).fail(() => { unlock(); $j.Ui.Dialog.alert('요청 실패'); });
        });
    }

    /* ── 직접 등록한 업체 ──────────────────────────── */

    function customFields(t = {}) {
        return `<label class="hc-wallet__price"><span>이름 (필수)</span>
                    <input type="text" class="form-control form-control-sm hc-wallet__note" data-c-name maxlength="40"
                           value="${esc(t.name || '')}" placeholder="예: 세금계산서 발행"></label>
                <label class="hc-wallet__price"><span>업체</span>
                    <input type="text" class="form-control form-control-sm hc-wallet__note" data-c-vendor maxlength="40"
                           value="${esc(t.vendor || '')}" placeholder="예: 볼타"></label>
                <label class="hc-wallet__price"><span>용도</span>
                    <input type="text" class="form-control form-control-sm hc-wallet__note" data-c-use maxlength="300"
                           value="${esc(t.use || '')}" placeholder="무엇에 쓰는 연동인지"></label>`;
    }

    const customValues = ($b) => ({
        custom_name: $b.find('[data-c-name]').val().trim(),
        custom_vendor: $b.find('[data-c-vendor]').val().trim(),
        custom_use: $b.find('[data-c-use]').val().trim(),
    });

    /** 등록 → 곧바로 그 업체의 설정 모달로 (과금·연동 정보를 이어서 적는다) */
    function openCustomAdd() {
        const m = $j.Ui.Modal.open({
            title: '업체 추가',
            html: `<div class="hc-wmodal">
                <section class="hc-set">
                    ${customFields()}
                    <div class="hc-wallet__hint">호출 기록이 없어 살아 있는지는 판정하지 않는다. 과금 방식과 연동 정보만 둔다.</div>
                </section>
                <div class="hc-doc__bar">
                    <button type="button" class="btn btn-sm btn-primary" data-c-save>등록</button>
                    <button type="button" class="btn btn-sm btn-default" data-c-cancel>닫기</button>
                </div>
            </div>`,
            width: '560px',
            maxWidth: 640,
        });
        const $b = m.el;
        $b.find('[data-c-name]').trigger('focus');
        $b.on('click', '[data-c-cancel]', () => m.close());
        $b.on('click', '[data-c-save]', function () {
            const v = customValues($b);
            if (!v.custom_name) return $j.Ui.Dialog.alert('이름을 넣어주세요');
            const $btn = $(this).prop('disabled', true);
            $.post('/adm/ajax/dev/health/custom/add', v).done((res) => {
                if (!res.result) return $j.Ui.Dialog.alert(res.message || '등록하지 못했습니다');
                TARGETS.push(res.target);
                renderSkeleton();
                m.close();
                loadStatus().then(() => openSettings(res.target.key));
            }).fail(() => $j.Ui.Dialog.alert('요청 실패'))
              .always(() => $btn.prop('disabled', false));
        });
    }

    $('#hcAddCustom').on('click', openCustomAdd);

    $(document).on('click', '[data-custom-del]', async function () {
        const $b = $(this).closest('.hc-wmodal');
        const key = $b.data('custom-key');
        const t = TARGETS.find((x) => x.key === key);
        if (!t || !(await $j.Ui.Dialog.confirm(`${t.name} 을(를) 삭제합니다.\n과금 방식과 연동 정보도 함께 지워지고 되돌릴 수 없습니다.`))) return;
        $.post('/adm/ajax/dev/health/custom/delete', { key }).done((res) => {
            if (!res.result) return $j.Ui.Dialog.alert(res.message || '삭제하지 못했습니다');
            TARGETS.splice(TARGETS.indexOf(t), 1);
            delete DOCS[key];
            $b.find('[data-set-cancel]').trigger('click');
            renderSkeleton();
            loadStatus();
            $j.F.Admin.Util.adminToast('success', '삭제했습니다');
        }).fail(() => $j.Ui.Dialog.alert('요청 실패'));
    });

    $('#hcList').on('click', 'tbody tr[data-key]', function () {
        openSettings($(this).data('key'));
    });

    /** 보기 전환 — 기본은 목록(요금). 카드를 고르면 주소에 남겨 새로고침해도 그 보기로 열린다(?view=card) */
    function setView(v) {
        $('#hcView [data-view]').each((i, el) => $(el).toggleClass('is-on', el.dataset.view === v));
        $('#hcGrid').prop('hidden', v !== 'card');
        $('#hcList').prop('hidden', v !== 'list');
        const u = new URL(location.href);
        if (v === 'card') u.searchParams.set('view', 'card'); else u.searchParams.delete('view');
        history.replaceState(null, '', u);
    }
    $('#hcView').on('click', '[data-view]', function () { setView(this.dataset.view); });
    $(document).on('click', '.hc-wrap [data-goto-list]', () => setView('list'));

    /* ── 연동 정보 ────────────────────────────────── */

    // 메모 한 덩어리다 — 공개·비공개로 칸을 나누지 않는다. 이 화면 자체가 개발팀·대표만 열린다(DevTeamGuard).
    // 카드에서는 보기만 하고 고치는 것은 설정 모달이다 — 연동 설정은 한 창에서 한다
    function docHtml() {
        return `<div class="hc-doc">
                    <div data-doc-memo></div>
                    <div class="hc-doc__bar hc-doc__bar--gap">
                        <button type="button" class="btn btn-xs btn-default" data-doc-edit><i class="fas fa-cog"></i> 설정</button>
                        <span class="hc-doc__meta" data-doc-meta></span>
                    </div>
                </div>`;
    }

    function fillDoc(key) {
        const d = DOCS[key] || {};
        const $c = $(`.hc-card[data-key="${key}"]`);
        const memo = (d.memo || '').trim();

        $c.find('[data-doc-memo]').html(memo
            ? `<pre>${esc(memo)}</pre>`
            : '<div class="hc-doc__empty">연동 정보 없음 — [설정] 에서 적어둔다</div>');
        $c.find('[data-doc-meta]').text(d.updated_at ? '수정 ' + d.updated_at : '');
    }

    function renderSkeleton() {
        $('#hcGrid').html(TARGETS.map((t) => `
            <div class="hc-card" data-key="${esc(t.key)}">
                <div class="hc-head">
                    <div><div class="hc-name">${esc(t.name)}</div><div class="hc-vendor">${esc(t.vendor)}</div></div>
                    <span class="hc-head__r"><span data-wchip></span><span class="hc-state is-wait" data-state>${STATE_LABEL.wait}</span></span>
                </div>
                <p class="hc-use">${esc(t.use)}</p>
                ${docHtml()}
                <div class="hc-msg" data-msg></div>
                <dl class="hc-stats" data-stats></dl>
                <div data-fail></div>
                ${probeHtml(t)}
            </div>`).join(''));
        TARGETS.forEach((t) => fillDoc(t.key));
    }

    /* ── 기록 판정 ────────────────────────────────── */

    function statsHtml(key, st) {
        const s = st.stats;
        if (!s) return '';
        const row = (k, v) => `<dt>${k}</dt><dd>${v}</dd>`;
        if (key === 'firebase') {
            return row('대기', `${s.pending}건` + (s.pending ? ` · 가장 오래된 건 ${s.wait_min}분째` : ''))
                 + row('처리중 멈춤', `${s.stuck}건`)
                 + row('마지막 처리', esc(s.last_done_at || '-'));
        }
        // 같은 연동의 다른 방향 기록(통보 수신 등) — 한 줄씩 덧붙인다
        const subs = (st.subs || []).map((x) => row(esc(x.label),
            `<span class="hc-state is-${x.state}">${STATE_LABEL[x.state] || x.state}</span> `
            + `${esc(x.last_at || '기록 없음')}${x.last_at ? ` <span class="hc-cost">(${ago(x.idle_min)})</span>` : ''}`)).join('');
        return row('마지막 기록', `${esc(s.last_at || '-')} <span class="hc-cost">(${ago(s.idle_min)})</span>`)
             + row('마지막 성공', esc(s.last_ok_at || '-'))
             + row('최근 ' + s.sample + '건', `실패 ${s.fail}건`)
             + subs;
    }

    function failHtml(st) {
        const f = st.stats && st.stats.last_fail;
        if (!f) return '';
        return `<details><summary>마지막 실패 · ${esc(f.at)} · #${f.num} · ${esc(f.result)}</summary><pre>${esc(f.body)}</pre></details>`;
    }

    function applyStatus(items) {
        const count = {}, wallets = {};
        Object.keys(items).forEach((key) => {
            const st = items[key];
            const $c = $(`.hc-card[data-key="${key}"]`);
            count[st.state] = (count[st.state] || 0) + 1;

            $c.removeClass('is-ok is-warn is-bad').addClass('is-' + st.state);
            // 카드는 메모 자리라 잔고는 **문제가 있을 때만** 머리에 한 마디 — 누르면 목록(요금) 보기로
            const w = gauge(st);
            const low = w && (w.state === 'warn' || w.state === 'bad');
            const what = w === st.stock || (w && w.meter === 'count') ? '잔여' : '잔고';
            if (low) wallets[w.state] = (wallets[w.state] || 0) + 1;
            $c.find('[data-wchip]').html(low
                ? `<button type="button" class="hc-state hc-wchip is-${w.state}" data-goto-list title="${esc(w.message)}">${what} ${W_STATE[w.state]}</button>`
                : '');
            $c.find('[data-state]').attr('class', 'hc-state is-' + st.state).text(STATE_LABEL[st.state] || st.state);
            $c.find('[data-msg]').text(st.message || '');
            $c.find('[data-stats]').html(statsHtml(key, st));
            $c.find('[data-fail]').html(failHtml(st));
        });

        $('#hcSummary').html(['bad', 'warn', 'ok', 'none', 'off', 'plan', 'info']
            .filter((s) => count[s])
            .map((s) => `<span class="hc-state is-${s}">${STATE_LABEL[s]} ${count[s]}</span>`).join('')
            + ['bad', 'warn'].filter((s) => wallets[s])
                .map((s) => `<button type="button" class="hc-state hc-wchip is-${s}" data-goto-list>잔고·잔여 ${W_STATE[s]} ${wallets[s]}</button>`).join(''));
        $('#hcUpdated').text('기록 기준 ' + new Date().toLocaleTimeString());
        ITEMS = items;
        renderList();
    }

    function loadStatus() {
        return $.post('/adm/ajax/dev/health/status', {}, (res) => {
            if (!res.result) return $j.Ui.Dialog.alert(res.message || '불러오지 못했습니다');
            applyStatus(res.items);
        });
    }

    /* ── 점검 호출 ────────────────────────────────── */

    function showProbe($card, p) {
        let detail = '';
        if (p.detail) {
            detail = `<details><summary class="hc-sum-plain">응답 보기</summary><pre>${esc(JSON.stringify(p.detail, null, 2))}</pre></details>`;
        }
        $card.find('[data-result]').html(
            `<div class="hc-r-head ${p.ok ? 'is-ok' : 'is-bad'}">${p.ok ? '성공' : '실패'}<span class="hc-r-ms">${p.ms}ms · ${new Date().toLocaleTimeString()}</span></div>
             <div>${esc(p.message)}</div>${detail}`);
    }

    function runProbe(key) {
        const $card = $(`.hc-card[data-key="${key}"]`);
        const $btn = $card.find('[data-probe]').prop('disabled', true);
        $card.find('[data-result]').text('호출 중…');
        return $.post('/adm/ajax/dev/health/probe', { key: key })
            .done((res) => res.result ? showProbe($card, res.probe) : $card.find('[data-result]').text(res.message))
            .fail(() => $card.find('[data-result]').text('요청 실패'))
            .always(() => $btn.prop('disabled', false));
    }

    $('#hcGrid').on('click', '[data-probe]', async function () {
        const key = $(this).closest('.hc-card').data('key');
        const t = TARGETS.find((x) => x.key === key);
        if (t.probe === 'button' && !(await $j.Ui.Dialog.confirm(`${t.name} 을(를) 실제로 호출합니다.\n${t.cost}`))) return;
        await runProbe(key);
        loadStatus(); // 점검 호출도 기록에 남으므로 판정을 다시 받는다
    });

    $('#hcRunAuto').on('click', async function () {
        const $btn = $(this).prop('disabled', true);
        await Promise.all(TARGETS.filter((t) => t.probe === 'auto').map((t) => runProbe(t.key)));
        await loadStatus();
        $btn.prop('disabled', false);
    });

    $('#hcReload').on('click', loadStatus);

    /* ── OCR ─────────────────────────────────────── */

    $('#hcGrid').on('click', '[data-ocr-run]', function () {
        const $card = $(this).closest('.hc-card');
        const file = $card.find('[data-ocr-file]')[0].files[0];
        if (!file) return $j.Ui.Dialog.alert('서류 사진을 골라주세요');

        const fd = new FormData();
        fd.append('file', file);
        fd.append('type', $card.find('[data-ocr-type]').val());

        const $btn = $(this).prop('disabled', true);
        $card.find('[data-result]').text('읽는 중…');
        $.ajax({ url: '/adm/ajax/dev/health/ocr', type: 'POST', data: fd, processData: false, contentType: false })
            .done((res) => {
                if (!res.result) return $card.find('[data-result]').text(res.message);
                const p = res.probe;
                showProbe($card, { ok: p.ok, ms: p.ms, message: p.message });
                if (p.ok) {
                    $card.find('[data-result]').append(
                        `<pre>${esc(JSON.stringify(p.detail.fields, null, 2))}</pre>
                         <details><summary class="hc-sum-plain">인식 글자 전체</summary><pre>${esc(p.detail.text)}</pre></details>`);
                }
                loadStatus();
            })
            .fail(() => $card.find('[data-result]').text('요청 실패'))
            .always(() => $btn.prop('disabled', false));
    });

    /* ── 연동 정보 — 카드의 [설정] 은 설정 모달의 연동 정보 칸으로 ── */

    $('#hcGrid').on('click', '[data-doc-edit]', function () {
        openSettings($(this).closest('.hc-card').data('key'), 'doc');
    });

    renderSkeleton();
    setView(new URLSearchParams(location.search).get('view') === 'card' ? 'card' : 'list');
    loadStatus();
}
